import Parameter from "./parameter";
import Msg from "./message";
import AppSettings from "./app-settings";
import IGdprSettings from "../interface/gdpr-settings";
import IEmailSignature from "../interface/email-signature";
import IEmailSettings from "../interface/email-settings";
import IEmailCustomTag from "../interface/email-custom-tag";
import IEmailButton from "../interface/email-button";
import ParamStorage from "../enum/param-storage";
import Message from "../enum/message";
import AppSetting from "../enum/app-setting";
import ParameterStorage from "../class/parameter-storage";

class EmailSettingsSingleton {

    public async getAll(): Promise<IEmailSettings> {
        return {
            buttons: this.btnGetAll(),
            signatures: await this.signLoadAll(),
            gdpr: this.gdprTextGet(),
            tags: await this.tagLoadAll()
        }
    }

    public async saveAll(settings: IEmailSettings): Promise<void> {
        await this.btnUpdate(settings.buttons);
        await this.signBulkAll(settings.signatures);
        await this.gdprTextSet(settings.gdpr);
        await this.tagBulkAll(settings.tags);
    }

    /* Public Methods - Custom Tags */

    public tagGetAll(): IEmailCustomTag[] {
        return this.getStoreCustomTags().getAll();
    }

    public async tagLoadAll(): Promise<IEmailCustomTag[]> {
        return await this.getStoreCustomTags().loadAll();
    }

    public async tagBulkAll(tags: IEmailCustomTag[]): Promise<IEmailCustomTag[]> {
        return await this.getStoreCustomTags().bulkAll(tags);
    }

    public async tagLoad(id: number): Promise<IEmailCustomTag | null> {
        return await this.getStoreCustomTags().load(id);
    }

    public async tagCreate(tag: IEmailCustomTag): Promise<IEmailCustomTag> {
        return await this.getStoreCustomTags().create(tag);
    }

    public async tagUpdate(tag: IEmailCustomTag): Promise<IEmailCustomTag> {
        return await this.getStoreCustomTags().update(tag);
    }

    public async tagDelete(id: number): Promise<number> {
        return await this.getStoreCustomTags().delete(id);
    }

    /* Public Methods - Buttons */

    public btnGetAll(): IEmailButton[] {
        return this.getStoreButtons().getAll();
    }

    public async btnCreate(btn: IEmailButton): Promise<IEmailButton> {
        return await this.getStoreButtons().create(btn);
    }

    public async btnUpdate(btns: IEmailButton[]): Promise<IEmailButton[]> {
        for (var i = 0; i < btns.length; i++) {
            await this.getStoreButtons().update(btns[i]);
        }
        return btns;
    }

    public async btnDelete(id: number): Promise<number> {
        return await this.getStoreButtons().delete(id);
    }

    public async btnCreateDefault(): Promise<void> {
        await this.btnCreate({
            colorBtn: '#415dd4',
            colorTxt: '#ffffff'
        });
        await this.btnCreate({
            colorBtn: '#4cae4c',
            colorTxt: '#ffffff'
        });
        await this.btnCreate({
            colorBtn: '#ca4330',
            colorTxt: '#ffffff'
        });
    }

    /* Public Methods - Signatures */

    public signGetAll(): IEmailSignature[] {
        return this.getStoreSignatures().getAll();
    }

    public async signLoadAll(): Promise<IEmailSignature[]> {
        return await this.getStoreSignatures().loadAll();
    }

    public async signBulkAll(signs: IEmailSignature[]): Promise<IEmailSignature[]> {
        return await this.getStoreSignatures().bulkAll(signs);
    }

    public async signLoad(id: number): Promise<IEmailSignature | null> {
        return await this.getStoreSignatures().load(id);
    }

    public async signCreate(sign: IEmailSignature): Promise<IEmailSignature> {
        return await this.getStoreSignatures().create(sign);
    }

    public async signUpdate(sign: IEmailSignature): Promise<IEmailSignature> {
        return await this.getStoreSignatures().update(sign);
    }

    public async signDelete(id: number): Promise<number> {
        return await this.getStoreSignatures().delete(id);
    }

    /* Public Methods - GDPR */

    public gdprTextGet(): IGdprSettings {
        return {
            allow: AppSettings.get(AppSetting.GdprAllow) || Msg.get(Message.gdprallow),
            decln: AppSettings.get(AppSetting.GdprDecln) || Msg.get(Message.gdprdecline)
        }
    }

    public async gdprTextSet(settings: IGdprSettings): Promise<IGdprSettings> {
        await AppSettings.set(AppSetting.GdprAllow, settings.allow);
        await AppSettings.set(AppSetting.GdprDecln, settings.decln);
        return settings;
    }

    /* Private Methods */

    private getStoreCustomTags(): ParameterStorage<IEmailCustomTag> {
        return Parameter.get(ParamStorage.EmailCustomTags);
    }

    private getStoreButtons(): ParameterStorage<IEmailButton> {
        return Parameter.get(ParamStorage.EmailButtons);
    }

    private getStoreSignatures(): ParameterStorage<IEmailSignature> {
        return Parameter.get(ParamStorage.EmailSignatures);
    }

}

const EmailSettings = new EmailSettingsSingleton();

export default EmailSettings;